/*
 * Real-shopper Core Web Vitals, tagged onto the Microsoft Clarity session.
 * Clarity filters and recordings can then be cut by "cwv_inp = poor", by template,
 * or by the element behind a slow tap ("slow_tap"), which lab tests cannot show.
 * Thresholds follow web.dev: LCP 2.5s/4s, INP 200ms/500ms, CLS 0.1/0.25.
 * Observers are buffered, so loading this script deferred loses no entries.
 */
(() => {
  if (!('PerformanceObserver' in window)) return;
  const supported = PerformanceObserver.supportedEntryTypes || [];
  const template = document.currentScript?.dataset.template || '';
  const rank = { good: 0, 'needs-improvement': 1, poor: 2 };
  const sent = {};
  let slowTaps = 0;

  const rate = (value, good, poor) => (value <= good ? 'good' : value <= poor ? 'needs-improvement' : 'poor');

  // Clarity tags keep every value set, so a metric is only re-sent when it gets worse
  const tag = (key, value) => {
    if (typeof window.clarity !== 'function') return;
    if (key in sent && rank[value] <= rank[sent[key]]) return;
    sent[key] = value;
    window.clarity('set', key, value);
    if (value === 'poor') window.clarity('event', `${key}_poor`);
  };

  const describe = (node) => {
    if (!(node instanceof Element)) return 'unknown';
    const el = node.closest('button, a, input, label, select, summary, [role="button"]') || node;
    const id = el.id ? `#${el.id}` : '';
    const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/).slice(0, 2).join('.') : '';
    return `${el.tagName.toLowerCase()}${id}${cls ? `.${cls}` : ''}`.slice(0, 80);
  };

  const observe = (type, onEntry, options = {}) => {
    if (!supported.includes(type)) return;
    new PerformanceObserver((list) => list.getEntries().forEach(onEntry)).observe({ type, buffered: true, ...options });
  };

  // LCP stops updating at the first tap or key press, or when the tab is hidden
  let lcp = 0;
  let lcpDone = false;
  observe('largest-contentful-paint', (entry) => {
    lcp = entry.startTime;
  });
  const finishLcp = () => {
    // Clarity loads async; if it is not up yet the 15s fallback below sends it
    if (lcpDone || !lcp || typeof window.clarity !== 'function') return;
    lcpDone = true;
    tag('cwv_lcp', rate(lcp, 2500, 4000));
    if (template) window.clarity?.('set', 'template', template);
  };
  ['pointerdown', 'keydown'].forEach((type) => addEventListener(type, finishLcp, { once: true, capture: true }));

  // CLS: the worst session window (shifts under 1s apart, at most 5s long)
  let cls = 0;
  let windowValue = 0;
  let windowStart = 0;
  let lastShift = 0;
  observe('layout-shift', (entry) => {
    if (entry.hadRecentInput) return;
    if (entry.startTime - lastShift > 1000 || entry.startTime - windowStart > 5000) {
      windowValue = 0;
      windowStart = entry.startTime;
    }
    lastShift = entry.startTime;
    windowValue += entry.value;
    if (windowValue > cls) {
      cls = windowValue;
      tag('cwv_cls', rate(cls, 0.1, 0.25));
    }
  });

  // INP: tag each interaction slower than 200ms as it happens
  observe(
    'event',
    (entry) => {
      if (!entry.interactionId || entry.duration <= 200) return;
      tag('cwv_inp', rate(entry.duration, 200, 500));
      if (entry.duration > 500 && slowTaps < 3 && typeof window.clarity === 'function') {
        slowTaps += 1;
        window.clarity('set', 'slow_tap', `${describe(entry.target)} ${Math.round(entry.duration)}ms`);
      }
    },
    { durationThreshold: 200 }
  );

  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') finishLcp();
  });
  // A shopper who only reads never taps; report LCP once the page has settled
  setTimeout(finishLcp, 15000);
})();
